import { useTranslation } from 'react-i18next'
import { CountryData } from 'react-phone-input-2'
import { regex_string, regex_fiscal_code, regex_vat_number } from 'src/regex'
import {
  checkFiscalCode,
  compareDates,
  isValidDate,
  replaceDotPhoneFormat,
  reverseDate,
  ybirth_date,
  ybool,
  ybool_required,
  yemail_required,
  ystr,
  ystr_required,
} from 'src/utils'
import * as Yup from 'yup'

export const useValidationSchemasGroup = () => {
  const { t } = useTranslation()

  const stepGroup2 = Yup.object().shape({
    mainInsured: Yup.object().shape({
      name: ystr_required.matches(regex_string, t('commons.error_is_wrong')),
      surname: ystr_required.matches(regex_string, t('commons.error_is_wrong')),
      email: yemail_required,
      emailConfirm: yemail_required.test(
        'check_email',
        t('data_entry_step_2.error_equal_email'),
        (value, field) => {
          return field.parent.email?.toLowerCase()?.trim() === value?.toLowerCase()?.trim()
        }
      ),
      phone: Yup.object().shape({
        formattedPhone: ystr.test(
          'validate-insured-phone',
          t('commons.error_is_wrong'),
          function () {
            const { country, value } = this.parent

            const formattedValue = value?.replace(/\D/g, '')

            if (
              formattedValue?.length > 0 &&
              formattedValue !== (country as CountryData)?.dialCode &&
              replaceDotPhoneFormat((country as CountryData)?.format, formattedValue).includes(
                'X'
              ) &&
              formattedValue?.length < (country as CountryData)?.dialCode.length + 9
            ) {
              return false
            }

            return true
          }
        ),
      }),
      vatNumber: ystr.matches(regex_vat_number, t('commons.error_is_wrong')),
      nationality: ystr,
      cap: ystr.when('nationality', (item, schema) => {
        return item === 'IT'
          ? schema
            .required(t('commons.required'))
            .test('check_validation_cap', t('commons.error_is_wrong'), (value: string) => {
              return value && value?.length <= 10 ? true : false
            })
          : schema.nullable().notRequired()
      }),
      fiscalCodeQuestion: ystr_required,
      birthDate: ybirth_date
        .when('fiscalCodeQuestion', (item, schema) => {
          return item === 'no_id' ? schema.required(t('commons.required')) : schema
        })
        .test('check_date', t('data_entry_step_2.error_date'), (value) => {
          return compareDates(new Date(reverseDate(value)), new Date()) <= 0
        })
        .test('check_validation', t('data_entry_step_2.error_date'), (value, field) => {
          return field.schema.exclusiveTests.required === true
            ? isValidDate(reverseDate(value))
            : true
        }),
      birthNation: ystr
        .when('fiscalCodeQuestion', (item, schema) => {
          return item === 'no_id' ? schema.required(t('commons.required')) : schema
        })
        .matches(regex_string, t('commons.error_is_wrong')),
      birthPlace: ystr
        .when('fiscalCodeQuestion', (item, schema) => {
          return item === 'no_id' ? schema.required(t('commons.required')) : schema
        })
        .matches(regex_string, t('commons.error_is_wrong')),
      gender: ystr.when('fiscalCodeQuestion', (item, schema) => {
        return item === 'no_id' ? schema.required(t('commons.required')) : schema
      }),
      fiscalCode: ystr
        .when('fiscalCodeQuestion', (item, schema) => {
          return item === 'yes_id'
            ? schema
              .required(t('commons.required'))
              .matches(regex_fiscal_code, t('commons.error_is_wrong'))
            : schema.nullable().notRequired()
        })
        .test('check_validation', t('commons.fiscal_code_mismatch'), (value, field) => {
          if (field?.parent?.fiscalCodeQuestion === 'yes_id') {
            return (
              checkFiscalCode(field?.parent?.name, field?.parent?.surname, value || '') || false
            )
          }
          return true
        }),
      consentPrivacy: ybool
        .required(t('commons.consent_required'))
        .oneOf([true], t('commons.consent_required')),
      consentMarketing: ybool,
      consentProfiling: ybool,
      consentCommunication: ybool,
    }),
  })

  const stepGroup3 = Yup.object().shape({
    mainInsured: Yup.object().shape({
      informativeVision: ybool_required,
      consentTd: ybool_required,
    }),
  })

  const stepGroup4 = Yup.object()

  return [Yup.object(), Yup.object(), stepGroup2, stepGroup3, stepGroup4]
}

export const useValidationSchemasGroup1 = () => {
  const { t } = useTranslation()

  return Yup.object().shape({
    name: ystr_required.matches(regex_string, t('commons.error_is_wrong')),
    surname: ystr_required.matches(regex_string, t('commons.error_is_wrong')),
    nationality: ystr_required,
    fiscalCodeQuestion: ystr_required,
    birthDate: ybirth_date
      .when('fiscalCodeQuestion', (item, schema) => {
        return item === 'no_id' ? schema.required(t('commons.required')) : schema
      })
      .test('check_date', t('data_entry_step_2.error_date'), (value) => {
        return compareDates(new Date(reverseDate(value)), new Date()) <= 0
      })
      .test('check_validation', t('data_entry_step_2.error_date'), (value, field) => {
        return field.schema.exclusiveTests.required === true
          ? isValidDate(reverseDate(value))
          : true
      }),
    birthNation: ystr
      .when('fiscalCodeQuestion', (item, schema) => {
        return item === 'no_id' ? schema.required(t('commons.required')) : schema
      })
      .matches(regex_string, t('commons.error_is_wrong')),
    birthPlace: ystr
      .when('fiscalCodeQuestion', (item, schema) => {
        return item === 'no_id' ? schema.required(t('commons.required')) : schema
      })
      .matches(regex_string, t('commons.error_is_wrong')),
    gender: ystr.when('fiscalCodeQuestion', (item, schema) => {
      return item === 'no_id' ? schema.required(t('commons.required')) : schema
    }),
    fiscalCode: ystr
      .when('fiscalCodeQuestion', (item, schema) => {
        return item === 'yes_id' ? schema.required(t('commons.required')) : schema
      })
      .matches(regex_fiscal_code, t('commons.error_is_wrong'))
      .test('check_validation', t('commons.fiscal_code_mismatch'), (value, field) => {
        if (field?.parent?.fiscalCodeQuestion === 'yes_id') {
          return checkFiscalCode(field?.parent?.name, field?.parent?.surname, value || '') || false
        }
        return true
      }),
  })
}
